/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { sessioniAPI } from '../api/sessioni';
import { useViewport } from '../hooks/useViewport';
import { fmtDurata } from '../utils/durata';
import { fmtDataOra } from '../utils/date';

// Conferma per annullare il timer di un tecnico dal pannello admin.
// Il tecnico si ritrovera' il timer sparito senza preavviso, quindi il motivo qui e'
// obbligatorio: e' l'unica spiegazione che resta, e finisce nel registro attivita'.
export default function AnnullaTimerModal({ sessione, onClose, onDone }) {
  const { isMobile } = useViewport();
  const [motivo, setMotivo] = useState('');
  const [err, setErr] = useState('');

  const elapsed = Math.max(0, Math.floor((Date.now() - new Date(sessione.inizio).getTime()) / 1000));
  const nomeTecnico = `${sessione.tecnico_nome || ''} ${sessione.tecnico_cognome || ''}`.trim();
  const motivoOk = motivo.trim().length > 0;

  const annullaMut = useMutation({
    mutationFn: () => sessioniAPI.annulla(sessione.id, motivo.trim()),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore durante l\'annullamento'),
  });

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.titolo}><AlertTriangle size={20} color="#B45309" /> Annulla timer di {nomeTecnico}</h3>

        <div style={styles.avviso}>
          Stai per annullare <strong>{fmtDurata(elapsed)}</strong>
          {sessione.codice ? <> su <strong>{sessione.codice}</strong></> : <> di lavoro libero</>}, partiti il {fmtDataOra(sessione.inizio)}.
          <div style={styles.avvisoSub}>Queste ore non verranno registrate da nessuna parte. L'operazione è definitiva.</div>
        </div>

        <div style={styles.nota}>
          Il tecnico non riceve nessun avviso: si accorgerà solo che il timer non c'è più.
          Se ha lavorato davvero, fatelo fermare a lui invece di annullarlo qui.
        </div>

        <div style={styles.field}>
          <label style={styles.lbl}>Motivo *</label>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            style={styles.input}
            placeholder="es. timer lasciato acceso a fine giornata"
            maxLength={500}
            autoFocus
          />
          <span style={styles.hint}>Resta nel registro dell'intervento insieme al tuo nome.</span>
        </div>

        {err && <div style={styles.errBox}>{err}</div>}

        <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
          <button onClick={onClose} disabled={annullaMut.isPending} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>
            Torna indietro
          </button>
          <button
            onClick={() => annullaMut.mutate()}
            disabled={!motivoOk || annullaMut.isPending}
            style={{ ...styles.dangerBtn, ...(isMobile ? styles.modalBtnMobile : {}), opacity: (!motivoOk || annullaMut.isPending) ? 0.5 : 1 }}
          >
            {annullaMut.isPending ? 'Annullo...' : 'Sì, annulla'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  overlayMobile: { padding: 0, alignItems: 'flex-end' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'auto' },
  modalMobile: { borderRadius: '16px 16px 0 0', padding: '1.25rem', maxWidth: '100%', maxHeight: '92vh' },
  titolo: { marginTop: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.1rem' },
  avviso: { padding: '0.85rem 1rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '10px', fontSize: '0.92rem', marginBottom: '0.85rem' },
  avvisoSub: { fontSize: '0.82rem', marginTop: '0.35rem', opacity: 0.9 },
  nota: { fontSize: '0.8rem', color: '#6B7280', lineHeight: 1.45, marginBottom: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.85rem' },
  lbl: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  hint: { fontSize: '0.75rem', color: '#9CA3AF' },
  errBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' },
  modalActionsMobile: { gap: '0.6rem' },
  modalBtnMobile: { flex: 1, padding: '0.85rem', fontSize: '0.95rem', textAlign: 'center', justifyContent: 'center' },
  cancelBtn: { padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  dangerBtn: { padding: '0.6rem 1.2rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
